import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { fmt, fmtPct } from '../utils';
import { useL } from '../LangContext';

export const NumCell = React.memo(function NumCell({
  value,
  redWhenNegative = false,
  redWhenPositive = false,
  bold = false,
  color: colorOverride,
}) {
  const num = Number(value) || 0;
  const isNeg = num < 0;
  const color =
    colorOverride ||
    (redWhenNegative && isNeg
      ? 'error.main'
      : redWhenPositive && num > 0
      ? 'error.main'
      : 'text.primary');
  return (
    <Typography
      variant="caption"
      sx={{
        color,
        fontWeight: bold ? 600 : 400,
        whiteSpace: 'nowrap',
        textAlign: 'right',
        display: 'block',
        fontSize: '0.65rem',
      }}
    >
      {fmt(num)}
    </Typography>
  );
});

export const PctCell = React.memo(function PctCell({
  value,
  redWhenNegative = false,
  redWhenOver = null,
  orangeWhenOver = null,
}) {
  const num = Number(value) || 0;
  const overLimit = redWhenOver !== null && num > redWhenOver;
  const overOrange =
    !overLimit && orangeWhenOver !== null && num > orangeWhenOver;
  const color = overLimit
    ? '#dc2626'
    : overOrange
    ? '#d97706'
    : redWhenNegative && num < 0
    ? 'error.main'
    : num > 0
    ? 'success.dark'
    : 'text.secondary';
  return (
    <Typography
      variant="caption"
      sx={{
        color,
        textAlign: 'right',
        whiteSpace: 'nowrap',
        display: 'block',
        fontSize: '0.65rem',
      }}
    >
      {fmtPct(num)}
    </Typography>
  );
});

// ── Inline display cells ──────────────────────────────────────────────────────

export function InlineNumberCell({ value }) {
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: 0.3,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.65rem',
          color:
            value >= 100
              ? '#059669'
              : value > 0
              ? 'primary.main'
              : 'text.secondary',
        }}
      >
        {Number(value || 0).toFixed(0)}%
      </Typography>
    </Box>
  );
}

export function InlineNoteCell({ value }) {
  return (
    <Box sx={{ minWidth: 80, maxWidth: 150 }}>
      <Typography
        variant="caption"
        color={value ? 'text.primary' : 'text.disabled'}
        sx={{
          fontStyle: value ? 'normal' : 'italic',
          fontSize: '0.65rem',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'block',
        }}
      >
        {value || '-'}
      </Typography>
    </Box>
  );
}

// ── ตรวจสอบรายวัน (review_checks) — checkbox ติ๊กของวันนี้ + hover ดูประวัติย้อนหลัง ─────

// สถานะ "หลอดไฟ": ตรวจวันนี้ = เขียว, ผ่านมา 1-2 วัน = เทา, ตั้งแต่ 3 วันขึ้นไป (หรือไม่เคยตรวจ) = แดง
const REVIEW_LAMP_COLOR = (daysAgo) => {
  if (daysAgo <= 0) return '#22c55e';
  if (daysAgo <= 2) return '#9ca3af';
  return '#dc2626';
};

export function ReviewCheckCell({ reviewChecks, todayStr }) {
  const L = useL();
  const checks = reviewChecks || [];
  const sorted = [...checks].sort((a, b) => b.date.localeCompare(a.date));
  const lastCheck = sorted[0];
  const recent = sorted.slice(0, 3);
  const daysAgo = lastCheck
    ? dayjs(todayStr).diff(dayjs(lastCheck.date), 'day')
    : Infinity;
  const tooltipTitle =
    sorted.length === 0 ? (
      <Box>
        <Box sx={{ fontWeight: 700, color: '#fca5a5' }}>
          {L('ขาดการตรวจ > 2 วัน', 'Not reviewed for > 2 days')}
        </Box>
        <Box>{L('ยังไม่มีประวัติการตรวจสอบ', 'No review history')}</Box>
      </Box>
    ) : (
      <Box>
        {daysAgo > 2 && (
          <Box sx={{ mb: 0.25, fontWeight: 700, color: '#fca5a5' }}>
            {L('ขาดการตรวจ > 2 วัน', 'Not reviewed for > 2 days')}
          </Box>
        )}
        {recent.map((c) => (
          <Box
            key={c.date}
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}
          >
            <span>{dayjs(c.date).format('DD/MM/YY')}</span>
            <span>{c.reviewedByName || 'ไม่ระบุ'}</span>
          </Box>
        ))}
        {sorted.length > 3 && <Box>...</Box>}
      </Box>
    );

  return (
    <Tooltip title={tooltipTitle} placement="top">
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'default',
        }}
      >
        <Box
          component="span"
          sx={{
            flexShrink: 0,
            display: 'inline-block',
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: REVIEW_LAMP_COLOR(daysAgo),
          }}
        />
        {lastCheck && (
          <Typography
            component="span"
            sx={{
              fontSize: '0.55rem',
              lineHeight: 1,
              color: 'primary.main',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            {(lastCheck.reviewedByName || '').slice(0, 8)}
            {daysAgo > 0 ? ` (${daysAgo}วัน)` : ''}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

// ── Inline amount (currency) ───────────────────────────────────────────────────

export function InlineAmountCell({ value, alwaysRed = false }) {
  return (
    <Box sx={{ textAlign: 'right' }}>
      <Typography
        variant="caption"
        sx={{
          whiteSpace: 'nowrap',
          color: alwaysRed
            ? 'error.main'
            : Number(value) > 0
            ? 'text.primary'
            : 'text.disabled',
        }}
      >
        {fmt(value || 0)}
      </Typography>
    </Box>
  );
}

export function ResizeHandle({ onStart }) {
  return (
    <Box
      component="span"
      onMouseDown={onStart}
      sx={{
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        width: 6,
        cursor: 'col-resize',
        zIndex: 20,
        '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' },
      }}
    />
  );
}
